import { useEffect, useMemo, useRef, useState } from 'react';
import { useHeight } from 'framework/utils';
import { VirtualTableRowType } from './types';
import type { ListTableGroup, ListTableGroupLoading, ListTableProps, ListTableRow } from './types';
import { getVirtualRows, getVirtualTableWindow, isGroupExpanded } from './utils';

const useListTableVirtualization = <Row extends ListTableRow>(props: ListTableProps<Row>) => {
  const {
    withChildren = false,
    childrenSource,
    virtualHeight,
    rowHeight = 48,
    groupRowHeight = 40,
    overscan = 5,
    hasMore = false,
    isLoadingMore = false,
    loadMore,
    loadMoreError,
    retryLoadMore,
    groups,
    rows,
  } = props;
  const { ref: containerRef, height: availableHeight } = useHeight<HTMLDivElement>();
  const [scrollTop, setScrollTop] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const resolvedVirtualHeight = virtualHeight ?? availableHeight;

  const rowsLoading = useMemo((): ListTableGroupLoading | undefined => {
    if (!hasMore || !loadMore) return undefined;
    return retryLoadMore
      ? { isLoadingMore, loadMore, error: loadMoreError, retry: retryLoadMore }
      : { isLoadingMore, loadMore };
  }, [hasMore, isLoadingMore, loadMore, loadMoreError, retryLoadMore]);

  const virtualRows = useMemo(
    () =>
      getVirtualRows({
        groups,
        rows,
        loading: rowsLoading,
        expandedGroups,
        withChildren,
        childrenSource,
        expandedRows,
      }),
    [expandedGroups, expandedRows, groups, rows, rowsLoading, withChildren, childrenSource],
  );
  const window = useMemo(
    () =>
      getVirtualTableWindow({
        rows: virtualRows,
        scrollTop,
        virtualHeight: resolvedVirtualHeight,
        rowHeight,
        groupRowHeight,
        overscan,
      }),
    [groupRowHeight, overscan, resolvedVirtualHeight, rowHeight, scrollTop, virtualRows],
  );

  const loadableTails = useMemo(
    () =>
      window.visibleRows.flatMap(item =>
        item.type === VirtualTableRowType.TAIL && !item.loading.isLoadingMore && !item.loading.error ? [item] : [],
      ),
    [window.visibleRows],
  );
  const loadableTailsRef = useRef(loadableTails);
  loadableTailsRef.current = loadableTails;
  const loadableTailsKey = loadableTails.map(({ group }) => (group ? `group:${group.id}` : 'rows')).join('|');
  useEffect(() => {
    loadableTailsRef.current.forEach(({ loading }) => loading.loadMore());
  }, [loadableTailsKey]);

  const toggleGroup = (group: ListTableGroup<Row>) => {
    const nextIsExpanded = !isGroupExpanded(group, expandedGroups);
    group.onExpandedChange?.(nextIsExpanded);
    if (group.isExpanded === undefined)
      setExpandedGroups(current => ({ ...current, [String(group.id)]: nextIsExpanded }));
  };

  const toggleRow = (key: string, isExpanded: boolean) =>
    setExpandedRows(current => ({ ...current, [key]: !isExpanded }));

  return { containerRef, setScrollTop, virtualRows, ...window, toggleGroup, toggleRow };
};

export default useListTableVirtualization;
